import type { CustomerSummary } from '@/lib/api/customers';

export type CustomerStatus = 'active' | 'inactive' | 'vip';

export interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  totalOrders: number;
  totalSpent: number;
  lastVisit: Date | null;
  averageOrderValue: number;
  status: CustomerStatus;
  loyaltyPoints: number;
  loyaltyTier: string | null;
  loyaltyState: string | null;
  marketingOptIn: boolean;
  marketingOptInAt: Date | null;
  marketingOptOutAt: Date | null;
  visitCount: number;
  visitStreak: number;
  avgVisitIntervalMs: number | null;
  joinDate: Date | null;
  preferredTable: string | null;
  orderType: 'dine-in' | 'pickup';
  currentTable: string | null;
  currency: string;
  dineInOrders: number;
  pickupOrders: number;
}

export const centsToAmount = (value: number | null | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return value / 100;
};

const formatTableLabel = (tableNumber: number | string | null | undefined): string | null => {
  if (tableNumber === null || tableNumber === undefined) {
    return null;
  }
  const numeric = typeof tableNumber === 'string' ? Number.parseInt(tableNumber, 10) : tableNumber;
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return `T-${String(numeric).padStart(2, '0')}`;
};

const determineStatus = (input: { totalOrders: number; lastVisit: Date | null }): CustomerStatus => {
  if (!input.lastVisit) {
    return 'inactive';
  }
  const now = Date.now();
  const daysSinceLastVisit = Math.floor((now - input.lastVisit.getTime()) / (1000 * 60 * 60 * 24));

  if (input.totalOrders >= 20 && daysSinceLastVisit <= 60) {
    return 'vip';
  }
  if (input.totalOrders >= 10 && daysSinceLastVisit <= 30) {
    return 'vip';
  }
  if (daysSinceLastVisit <= 45) {
    return 'active';
  }
  return 'inactive';
};

const computeLoyaltyPoints = (totalOrders: number, totalSpent: number) =>
  Math.max(0, Math.round(totalOrders * 25 + totalSpent * 0.5));

export const toDateFromMillis = (value: number | null): Date | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return new Date(value);
};

export const transformCustomer = (input: CustomerSummary): Customer => {
  const firstVisitTs =
    typeof input.firstVisit === 'string'
      ? Date.parse(input.firstVisit)
      : typeof input.firstOrderAt === 'number'
      ? input.firstOrderAt
      : null;
  const lastVisitTs =
    typeof input.lastVisitAt === 'number'
      ? input.lastVisitAt
      : typeof input.lastVisit === 'string'
      ? Date.parse(input.lastVisit)
      : typeof input.lastOrderAt === 'number'
      ? input.lastOrderAt
      : null;

  const joinDate = toDateFromMillis(firstVisitTs);
  const lastVisit = toDateFromMillis(lastVisitTs);

  const totalSpentCents =
    typeof input.lifetimeValueCents === 'number' && Number.isFinite(input.lifetimeValueCents)
      ? input.lifetimeValueCents
      : typeof input.totalSpentCents === 'number' && Number.isFinite(input.totalSpentCents)
      ? input.totalSpentCents
      : 0;
  const avgOrderValueCents =
    typeof input.averageOrderValueCents === 'number' && Number.isFinite(input.averageOrderValueCents)
      ? input.averageOrderValueCents
      : typeof input.avgOrderValueCents === 'number' && Number.isFinite(input.avgOrderValueCents)
      ? input.avgOrderValueCents
      : 0;

  const totalSpent = centsToAmount(totalSpentCents);
  const averageOrderValue = centsToAmount(avgOrderValueCents);
  const totalOrders =
    typeof input.visitCount === 'number' && Number.isFinite(input.visitCount)
      ? input.visitCount
      : typeof input.totalOrders === 'number' && Number.isFinite(input.totalOrders)
      ? input.totalOrders
      : 0;

  let status: CustomerStatus;
  if (input.loyaltyState === 'vip') {
    status = 'vip';
  } else if (input.loyaltyState === 'active') {
    status = 'active';
  } else if (input.loyaltyState === 'lapsed') {
    status = 'inactive';
  } else {
    status = determineStatus({ totalOrders, lastVisit });
  }
  const loyaltyPoints =
    typeof input.loyaltyPoints === 'number' && Number.isFinite(input.loyaltyPoints)
      ? Math.max(0, Math.round(input.loyaltyPoints))
      : computeLoyaltyPoints(totalOrders, totalSpent);

  const marketingOptIn = Boolean(input.marketingOptIn);
  const marketingOptInAt = toDateFromMillis(typeof input.marketingOptInAt === 'number' ? input.marketingOptInAt : null);
  const marketingOptOutAt = toDateFromMillis(typeof input.marketingOptOutAt === 'number' ? input.marketingOptOutAt : null);
  const visitStreak = typeof input.visitStreak === 'number' && Number.isFinite(input.visitStreak) ? input.visitStreak : 0;
  const avgVisitIntervalMs = typeof input.avgVisitIntervalMs === 'number' && Number.isFinite(input.avgVisitIntervalMs) ? input.avgVisitIntervalMs : null;

  const inferredOrderType =
    input.orderType === 'dine-in' || input.orderType === 'pickup'
      ? input.orderType
      : input.currentTable !== null && input.currentTable !== undefined
      ? 'dine-in'
      : 'pickup';

  const preferredTable = formatTableLabel(input.preferredTable);
  const currentTable = inferredOrderType === 'dine-in' ? formatTableLabel(input.currentTable) : null;
  const dineInOrders =
    typeof input.dineInOrders === 'number' && Number.isFinite(input.dineInOrders) ? input.dineInOrders : 0;
  const pickupOrders =
    typeof input.pickupOrders === 'number' && Number.isFinite(input.pickupOrders) ? input.pickupOrders : 0;

  return {
    id: input.id,
    name: input.name ?? 'Guest',
    email: input.email ?? null,
    phone: input.phone ?? null,
    totalOrders,
    totalSpent,
    lastVisit,
    averageOrderValue,
    status,
    loyaltyPoints,
    loyaltyTier: input.loyaltyTier ?? null,
    loyaltyState: input.loyaltyState ?? null,
    marketingOptIn,
    marketingOptInAt,
    marketingOptOutAt,
    visitCount: totalOrders,
    visitStreak,
    avgVisitIntervalMs,
    joinDate,
    preferredTable,
    orderType: inferredOrderType,
    currentTable,
    currency: input.currency ?? 'USD',
    dineInOrders,
    pickupOrders,
  };
};
